// NOTE: https://nextjs.org/docs/messages/next-router-not-mounted
import { useRouter } from 'next/compat/router';
import React, { createContext, ReactNode, useContext, useMemo } from 'react';

interface RedirectContextProps {
  redirectTo: (url: string) => void;
  redirectBaseUrl: string | undefined;
}

const RedirectContext = createContext<RedirectContextProps>({
  redirectTo: () => {
    /* empty by default */
  },
  redirectBaseUrl: undefined,
});

export const useRedirectContext = () => useContext(RedirectContext);

interface RedirectProviderProps {
  children: ReactNode;
  redirectBaseUrl?: string;
  isExternal?: boolean;
}

export const usePotentiallyRedirectedUrl = (originalUrl: string) => {
  const redirectContext = useRedirectContext();

  if (redirectContext.redirectBaseUrl) {
    return `${redirectContext.redirectBaseUrl}&url=${encodeURIComponent(originalUrl)}`;
  }

  return originalUrl;
};

export const RedirectProvider: React.FC<RedirectProviderProps> = ({
  children,
  redirectBaseUrl,
  isExternal,
}) => {
  const router = useRouter();

  const redirectTo = (url: string): void => {
    const link = redirectBaseUrl
      ? `${redirectBaseUrl}&url=${encodeURIComponent(url)}`
      : url;

    if (isExternal) {
      window.location.href = link;
    } else {
      router?.push(link);
    }
  };

  const contextValue = useMemo(
    () => ({
      redirectBaseUrl,
      redirectTo,
    }),
    [redirectBaseUrl]
  );

  return (
    <RedirectContext.Provider value={contextValue}>
      {children}
    </RedirectContext.Provider>
  );
};
